import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Download, Users, Calendar, RefreshCw, Lock } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminNewsletter = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Simple password protection (change this password!)
  const ADMIN_PASSWORD = 'huacaya2026';

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
      fetchSubscribers();
    } else {
      setError('Password errata');
    }
  };

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/newsletter/subscribers`);
      setSubscribers(response.data.subscribers || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching subscribers:', err);
      setError('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (subscribers.length === 0) return;
    
    const headers = ['Email', 'Data Iscrizione', 'Fonte', 'Attivo'];
    const rows = subscribers.map(s => [
      s.email,
      s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString('it-IT') : 'N/A',
      s.source || 'website',
      s.active ? 'Sì' : 'No'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `huacaya_subscribers_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <main className="pt-20 min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#1C1C1E] rounded-2xl p-8 border border-[#3A3A3C] max-w-md w-full mx-4"
        >
          <div className="flex items-center gap-3 mb-6">
            <Lock className="w-6 h-6 text-[#C9B896]" />
            <h1 className="text-xl font-bold text-white">Admin Area</h1>
          </div>
          
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full px-4 py-3 bg-[#2C2C2E] border border-[#3A3A3C] rounded-lg text-white placeholder-[#86868B] focus:outline-none focus:border-[#C9B896] mb-4"
            />
            {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
            <button
              type="submit"
              className="w-full py-3 bg-[#C9B896] text-white rounded-lg font-medium hover:bg-[#A89770] transition-colors"
            >
              Accedi
            </button>
          </form>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="pt-20 min-h-screen bg-[#0A0A0A]">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Mail className="w-8 h-8 text-[#C9B896]" />
              <h1 className="text-2xl font-bold text-white">Newsletter Subscribers</h1>
            </div>
            <div className="flex gap-3">
              <button
                onClick={fetchSubscribers}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-[#2C2C2E] text-white rounded-lg hover:bg-[#3A3A3C] transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Aggiorna
              </button>
              <button
                onClick={exportToCSV}
                disabled={subscribers.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-[#C9B896] text-white rounded-lg hover:bg-[#A89770] transition-colors disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                Esporta CSV
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-[#1C1C1E] rounded-xl p-6 border border-[#3A3A3C]">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-[#C9B896]" />
                <span className="text-[#86868B]">Totale Iscritti</span>
              </div>
              <p className="text-3xl font-bold text-white mt-2">{total}</p>
            </div>
            <div className="bg-[#1C1C1E] rounded-xl p-6 border border-[#3A3A3C]">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-[#C9B896]" />
                <span className="text-[#86868B]">Ultimo Aggiornamento</span>
              </div>
              <p className="text-lg text-white mt-2">
                {new Date().toLocaleString('it-IT')}
              </p>
            </div>
          </div>

          {/* Subscribers Table */}
          <div className="bg-[#1C1C1E] rounded-xl border border-[#3A3A3C] overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-8 h-8 text-[#C9B896] animate-spin mx-auto" />
                <p className="text-[#86868B] mt-2">Caricamento...</p>
              </div>
            ) : subscribers.length === 0 ? (
              <div className="p-8 text-center">
                <Mail className="w-12 h-12 text-[#3A3A3C] mx-auto mb-4" />
                <p className="text-[#86868B]">Nessun iscritto ancora</p>
                <p className="text-sm text-[#5A5A5C] mt-1">
                  Gli iscritti appariranno qui quando qualcuno si registra alla newsletter
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-[#2C2C2E]">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#86868B]">Email</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#86868B]">Data</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#86868B]">Fonte</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#86868B]">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((subscriber, index) => (
                    <tr 
                      key={subscriber.email}
                      className="border-t border-[#2C2C2E] hover:bg-[#2C2C2E]/50 transition-colors"
                    >
                      <td className="px-6 py-4 text-white">{subscriber.email}</td>
                      <td className="px-6 py-4 text-[#A1A1A6]">
                        {subscriber.subscribed_at 
                          ? new Date(subscriber.subscribed_at).toLocaleDateString('it-IT')
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-[#A1A1A6]">
                        {subscriber.source || 'website'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          subscriber.active 
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {subscriber.active ? 'Attivo' : 'Inattivo'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </motion.div>
      </div>
    </main>
  );
};

export default AdminNewsletter;
